<template>
  <app-modal
    v-bind="$attrs"
    title="確認"
    ref="modal"
    class="modal-container"
    content-class="modal-full"
  >
    <template v-slot:body="{ ok, cancel }">
      <v-card-text class="py-4 px-0">
        <div class="modal-body">
          <v-container fluid class="pa-1">
            <div class="font-weight-bold mb-3">
              ポリシーを設定反映します。よろしいですか？
            </div>
            <v-row>
              <v-col cols="12">
                <app-label-group
                  label="VPNコード"
                  :value="contractInfo.vpnCode"
                />
              </v-col>
              <v-col cols="12">
                <app-label-group
                  label="セキュリティアプライアンス"
                  :value="`イントラネットFW(${contractInfo.intranetFwBandwidth.value}${$filter.enumTo(contractInfo.intranetFwBandwidth.unit, 'bandwidth_unit')})`"
                />
              </v-col>
            </v-row>
            <template v-for="(zonePolicy, i) in displayPolicyList" :key="i">
              <div class="font-weight-bold mt-4">
                {{
                  $filter.enumTo(zonePolicy.srcZoneType, "zoneType") +
                  "/" +
                  zonePolicy.srcZoneName +
                  " → " +
                  $filter.enumTo(zonePolicy.dstZoneType, "zoneType") +
                  "/" +
                  zonePolicy.dstZoneName
                }}
              </div>
              <v-table density="compact">
                <thead class="v-table-head text-caption">
                  <tr>
                    <th>ID</th>
                    <th>送信元アドレス</th>
                    <th>宛先アドレス</th>
                    <th>サービス</th>
                    <th>アクション</th>
                    <th>有効/無効</th>
                    <th>備考</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(policy, i) in zonePolicy.policyList"
                    :key="i"
                    :class="{
                      'gray-out':
                        policy.policyCategoryInfo ===
                        'UNEDITABLE_DEFAULT_POLICY',
                    }"
                  >
                    <td>{{ policy.policyId }}</td>
                    <td>
                      <intranet-policy-src-address
                        :ip-address-list="policy.srcAddressList"
                      />
                    </td>
                    <td>
                      <intranet-policy-dst-address
                        :ip-address-list="policy.dstAddressList"
                      />
                    </td>
                    <td>
                      <intranet-policy-service
                        :default-service-list="policy.serviceList"
                        :custom-service-list="policy.customServiceList"
                      />
                    </td>
                    <td>
                      {{ $filter.enumTo(policy.actionType, "allow_deny") }}
                    </td>
                    <td>
                      {{ $filter.enumTo(policy.isPolicyStatus, "enable") }}
                    </td>
                    <td class="text-pre-wrap">
                      {{ $filter.emptyTo(policy.description, "-") }}
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </template>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn color="primary" variant="flat" @click="ok">はい</v-btn>
            <v-btn color="primary" variant="outlined" @click="cancel"
              >いいえ
            </v-btn>
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import IntranetPolicySrcAddress from "@/components/policy/IntranetPolicySrcAddress.vue";
import IntranetPolicyDstAddress from "@/components/policy/IntranetPolicyDstAddress.vue";
import IntranetPolicyService from "@/components/policy/IntranetPolicyService.vue";
import type {
  IntranetFwPolicy,
  ZonePolicyInfo,
} from "@/apis/IntranetfwPolicyApi";
import type { ZoneRelationItem } from "@/apis/ZoneRelationApi";
import type { IntranetfwServiceMenu } from "@/apis/IntranetfwServiceMenuApi";
import AppLabelGroup from "@/components/AppLabelGroup.vue";

/**
 * 設定反映画面表示用のオブジェクト
 * ゾーンの接続とポリシーの情報を結合した形に
 */
type ZonePolicy = Pick<ZonePolicyInfo, "policyList"> &
  Pick<
    ZoneRelationItem,
    "srcZoneType" | "srcZoneName" | "dstZoneType" | "dstZoneName"
  >;

export default defineComponent({
  name: "IntranetFwPolicyConfirm",
  components: {
    AppLabelGroup,
    IntranetPolicySrcAddress,
    IntranetPolicyDstAddress,
    IntranetPolicyService,
  },
  props: {
    /** ポリシー */
    intranetFwPolicy: {
      type: Object as PropType<IntranetFwPolicy>,
      required: true,
    },
    /** ゾーン接続 */
    zoneList: {
      type: Array as PropType<ZoneRelationItem[]>,
      required: true,
    },
    /** イントラネット契約情報 */
    contractInfo: {
      type: Object as PropType<
        Pick<IntranetfwServiceMenu, "vpnCode" | "intranetFwBandwidth">
      >,
      required: true,
    },
  },
  computed: {
    /** 設定反映画面表示用に整形した情報 */
    displayPolicyList(): ZonePolicy[] {
      // 各方向のゾーン + ポリシー情報を1つの配列として返却
      return [
        ...this.intranetFwPolicy.extraToPrivateList.map((v) => {
          const zone = this.zoneList.find(
            (e) => v.zoneRelationId === e.zoneRelationId
          )!;
          return {
            policyList: v.policyList,
            srcZoneType: zone.srcZoneType,
            srcZoneName: zone.srcZoneName,
            dstZoneType: zone.dstZoneType,
            dstZoneName: zone.dstZoneName,
          };
        }),
        ...this.intranetFwPolicy.privateToExtraList.map((v) => {
          const zone = this.zoneList.find(
            (e) => v.zoneRelationId === e.zoneRelationId
          )!;
          return {
            policyList: v.policyList,
            srcZoneType: zone.srcZoneType,
            srcZoneName: zone.srcZoneName,
            dstZoneType: zone.dstZoneType,
            dstZoneName: zone.dstZoneName,
          };
        }),
        ...this.intranetFwPolicy.privateToPrivateList.map((v) => {
          const zone = this.zoneList.find(
            (e) => v.zoneRelationId === e.zoneRelationId
          )!;
          return {
            policyList: v.policyList,
            srcZoneType: zone.srcZoneType,
            srcZoneName: zone.srcZoneName,
            dstZoneType: zone.dstZoneType,
            dstZoneName: zone.dstZoneName,
          };
        }),
      ].sortBy(["srcZoneType", "asc"], ["srcZoneName", "asc"]);
    },
  },
});
</script>
<style lang="scss" scoped>
th {
  text-align: center !important;
}
td {
  min-width: 60px !important;
}
</style>
